<template>
  <div class="recording-detected-modal" dir="rtl">

    <!-- Step 0: Call Type Selection -->
    <div v-if="step === 'callType'" class="step-call-type">
      <div class="step-header">
        <p class="recording-filename">{{ recording.filename }}</p>
      </div>

      <p class="call-type-label">מה סוג השיחה?</p>

      <div class="call-type-buttons">
        <button class="call-type-btn applicant-btn" @click="selectCallType('applicant')">
          <span class="call-type-icon">&#128100;</span>
          <span>שיחה עם מועמד</span>
        </button>
        <button class="call-type-btn employer-btn" @click="selectCallType('employer')">
          <span class="call-type-icon">&#127970;</span>
          <span>שיחה עם מעסיק</span>
        </button>
      </div>

      <div class="step-actions">
        <button
          v-if="onSnooze"
          class="btn btn-outline"
          @click="onSnooze()"
        >
          שאלי אותי בעוד דקה
        </button>
        <button
          class="btn btn-secondary"
          @click="skipRecording"
          :disabled="isSkipping"
        >
          {{ isSkipping ? 'מדלג...' : 'דלגי על שיחה זו' }}
        </button>
      </div>

      <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
    </div>

    <!-- Step 1: Candidate Search -->
    <div v-if="step === 'search'" class="step-search">
      <div class="step-header">
        <p class="recording-filename">{{ recording.filename }}</p>
      </div>

      <!-- Hint suggestion -->
      <div v-if="hintSuggestions.length > 0 && !selectedApplicant" class="hint-section">
        <span class="hint-label">אולי התכוונת ל:</span>
        <div class="hint-chips">
          <div
            v-for="hint in hintSuggestions"
            :key="hint.resource_id"
            class="hint-chip"
            @click="selectApplicant(hint)"
          >
            <span @click.stop>
              <link-entity
                :payload="hint"
                :additional_data="{ resource_type: 'Applicant' }"
              />
            </span>
            <span v-if="hint.phone_number" class="hint-phone">{{ hint.phone_number }}</span>
          </div>
        </div>
      </div>

      <!-- Search input -->
      <div v-if="!selectedApplicant" class="search-section">
        <input
          type="text"
          v-model="searchQuery"
          @input="onSearchInput"
          placeholder="חפשי מועמד לפי שם או טלפון..."
          class="search-input"
          ref="searchInput"
        />

        <div v-if="isSearching" class="search-loading">מחפש...</div>

        <div v-if="searchResults.length > 0" class="search-results">
          <div
            v-for="applicant in searchResults"
            :key="applicant.resource_id"
            class="search-result-item"
            @click="selectApplicant(applicant)"
          >
            <span class="result-name" @click.stop>
              <link-entity
                :payload="applicant"
                :additional_data="{ resource_type: 'Applicant' }"
              />
            </span>
            <span v-if="applicant.phone_number" class="result-phone">{{ applicant.phone_number }}</span>
            <span v-if="applicant.status" class="result-status">{{ applicant.status }}</span>
          </div>
        </div>

        <div v-if="searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && hasSearched" class="no-results">
          לא נמצאו תוצאות
        </div>
      </div>

      <!-- Selected candidate chip -->
      <div v-if="selectedApplicant" class="selected-section">
        <div class="selected-chip">
          <span>{{ selectedApplicant.first_name }} {{ selectedApplicant.last_name }}</span>
          <button class="chip-remove" @click="clearSelection">&times;</button>
        </div>
      </div>

      <div class="step-actions">
        <button
          v-if="selectedApplicant"
          class="btn btn-primary"
          @click="goToCallSubtype"
        >
          המשך
        </button>
        <button
          class="btn btn-secondary"
          @click="skipRecording"
          :disabled="isSkipping"
        >
          {{ isSkipping ? 'מדלג...' : 'דלגי על שיחה זו' }}
        </button>
      </div>

      <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
    </div>

    <!-- Step 1.5: Call Subtype (follow-up vs introduction) -->
    <div v-if="step === 'call_subtype'" class="step-call-type">
      <div class="selected-chip compact">
        <span>{{ selectedApplicant.first_name }} {{ selectedApplicant.last_name }}</span>
        <button class="chip-remove" @click="backToSearch">&times;</button>
      </div>

      <p class="call-type-label">מה אופי השיחה?</p>

      <div class="call-type-buttons">
        <button class="call-type-btn" @click="selectCallSubtype('introduction')">
          <span class="call-type-icon">&#128075;</span>
          <span>שיחת הכרות</span>
          <span class="call-type-desc">ניתוח מלא + חילוץ פריטים</span>
        </button>
        <button class="call-type-btn" @click="selectCallSubtype('follow_up')">
          <span class="call-type-icon">&#128257;</span>
          <span>שיחת פולואו אפ</span>
          <span class="call-type-desc">סיכום שיחה בלבד</span>
        </button>
      </div>
    </div>

    <!-- Step 2: Offer Selection -->
    <div v-if="step === 'offer'" class="step-offer">
      <div class="selected-chip compact">
        <span>{{ selectedApplicant.first_name }} {{ selectedApplicant.last_name }}</span>
        <button class="chip-remove" @click="backToSearch">&times;</button>
      </div>

      <p class="offer-label">האם תרצי לשייך את השיחה להצעה?</p>

      <div v-if="isLoadingOffers" class="search-loading">טוען הצעות...</div>

      <div v-else-if="offers.length > 0" class="offer-list">
        <label
          v-for="offer in offers"
          :key="offer.id"
          class="offer-item"
          :class="{ 'offer-selected': selectedOfferId === offer.id }"
        >
          <input
            type="radio"
            :value="offer.id"
            v-model="selectedOfferId"
            name="offer"
          />
          <span class="offer-info">
            <span class="offer-position">{{ (offer.position && offer.position.title) || ('הצעה #' + offer.id) }}</span>
            <span v-if="offer.position && offer.position.company" class="offer-company">{{ offer.position.company.name }}</span>
            <span class="offer-status">{{ offer.status }}</span>
          </span>
        </label>
      </div>
      <div v-else class="no-offers">אין הצעות פתוחות למועמד זה</div>

      <div class="step-actions">
        <button
          class="btn btn-primary"
          @click="submitForAnalysis"
        >
          {{ selectedOfferId ? 'שלחי לניתוח' : 'ללא הצעה - שלחי לניתוח' }}
        </button>
      </div>
    </div>

    <!-- Employer Step: Company Search + Contact Selection -->
    <div v-if="step === 'employer_search'" class="step-employer-search">
      <div class="step-header">
        <p class="recording-filename">{{ recording.filename }}</p>
      </div>

      <!-- Company search -->
      <div v-if="!selectedCompany" class="search-section">
        <input
          type="text"
          v-model="companySearchQuery"
          @input="onCompanySearchInput"
          placeholder="חפשי חברה לפי שם..."
          class="search-input"
          ref="companySearchInput"
        />

        <div v-if="isSearchingCompany" class="search-loading">מחפש...</div>

        <div v-if="companySearchResults.length > 0" class="search-results">
          <div
            v-for="company in companySearchResults"
            :key="company.resource_id"
            class="search-result-item"
            @click="selectCompany(company)"
          >
            <span class="result-name">{{ company.name }}</span>
            <span class="result-phone">#{{ company.resource_id }}</span>
          </div>
        </div>

        <div v-if="companySearchQuery.length >= 2 && !isSearchingCompany && companySearchResults.length === 0 && hasSearchedCompany" class="no-results">
          לא נמצאו תוצאות
        </div>
      </div>

      <!-- Selected company chip + contact selection -->
      <div v-if="selectedCompany" class="selected-section">
        <div class="selected-chip">
          <span>{{ selectedCompany.name }}</span>
          <button class="chip-remove" @click="clearCompanySelection">&times;</button>
        </div>

        <!-- Contact selection -->
        <div class="contact-section">
          <p class="contact-label">בחרי איש קשר:</p>

          <div v-if="isLoadingEmployees" class="search-loading">טוען אנשי קשר...</div>

          <div v-else-if="companyEmployees.length > 0" class="employee-list">
            <div
              v-for="emp in companyEmployees"
              :key="emp.id"
              class="employee-item"
              :class="{ 'employee-selected': selectedContact && selectedContact.id === emp.id }"
              @click="selectContact(emp)"
            >
              <span class="employee-name">{{ emp.name }}</span>
              <span v-if="emp.title" class="employee-title">{{ emp.title }}</span>
            </div>
          </div>
          <div v-else class="no-employees">לא נמצאו אנשי קשר</div>

          <div class="free-contact">
            <label class="free-contact-label">איש קשר אחר:</label>
            <input
              type="text"
              v-model="contactName"
              placeholder="שם איש קשר..."
              class="search-input contact-input"
              @input="selectedContact = null"
            />
          </div>
        </div>
      </div>

      <div class="step-actions">
        <button
          v-if="selectedCompany && (selectedContact || contactName.length > 0)"
          class="btn btn-primary"
          @click="submitEmployerCall"
        >
          שלחי לתמלול
        </button>
        <button
          class="btn btn-secondary"
          @click="step = 'callType'"
        >
          חזרה
        </button>
      </div>

      <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
    </div>

    <!-- Step 3: Processing -->
    <div v-if="step === 'processing'" class="step-processing">
      <div class="processing-indicator">
        <div class="spinner"></div>
        <p class="processing-text">מנתח את השיחה...</p>
        <p class="processing-sub">תמלול, זיהוי דוברים, חילוץ משפטים, סיווג, פענוח</p>
      </div>
    </div>

    <!-- Step 4: Review -->
    <div v-if="step === 'review'" class="step-review">
      <p class="review-title">
        אשרי / אל תאשרי את הפריטים הבאים שאותרו במהלך השיחה
        <span v-if="selectedApplicant" class="review-applicant-link">
          עם
          <link-entity
            :payload="selectedApplicant"
            :additional_data="{ resource_type: 'Applicant' }"
          />
        </span>
      </p>

      <div class="review-summary">
        <span>זוהו <strong>{{ reviewItems.length }}</strong> פריטים</span>
        <span v-if="resultStatementsCount"> | {{ resultStatementsCount }} משפטים</span>
      </div>

      <div class="table-wrapper">
        <table class="review-table">
          <thead>
            <tr>
              <th class="col-check">
                <input type="checkbox" :checked="allChecked" @change="toggleAll" />
              </th>
              <th class="col-type">סוג</th>
              <th class="col-name">שם</th>
              <th class="col-field">שדה</th>
              <th class="col-value">ערך</th>
              <th class="col-source">משפט מקור</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in reviewItems" :key="item.id" :class="{ 'row-unchecked': !item.checked }">
              <td class="col-check">
                <input type="checkbox" v-model="item.checked" />
              </td>
              <td class="col-type">{{ formatResourceType(item.resource_type) }}</td>
              <td class="col-name">{{ item.instance_name }}</td>
              <td class="col-field">{{ item.attribute_name }}</td>
              <td class="col-value">
                <span
                  v-if="isToggleable(item.value)"
                  class="value-toggle"
                  @click="toggleValue(item)"
                >{{ item.value }}</span>
                <input v-else type="text" v-model="item.value" class="value-input" />
              </td>
              <td class="col-source">
                <span class="source-text">{{ item._source_statement }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="review-actions">
        <button
          class="btn btn-primary"
          :disabled="isApproving"
          @click="approveItems"
        >
          {{ isApproving ? 'מעבד...' : 'אשרי (' + checkedCount + ')' }}
        </button>
      </div>

      <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
    </div>
  </div>
</template>

<script>
import LinkEntity from '@/components/LinkEntity.vue'

export default {
  name: 'RecordingDetectedModal',
  components: { LinkEntity },
  props: {
    recording: { type: Object, required: true },
    onComplete: { type: Function, required: true },
    onSubmitForAnalysis: { type: Function, default: null },
    onSnooze: { type: Function, default: null },
    startAtReview: { type: Boolean, default: false },
    initialReviewItems: { type: Array, default: function () { return [] } },
    initialStatementsCount: { type: Number, default: null },
    selectedApplicantData: { type: Object, default: null }
  },
  data: function () {
    return {
      step: 'callType',
      callType: null,
      callSubtype: null,
      searchQuery: '',
      searchResults: [],
      hintSuggestions: [],
      isSearching: false,
      hasSearched: false,
      selectedApplicant: null,
      offers: [],
      selectedOfferId: null,
      isLoadingOffers: false,
      isSkipping: false,
      reviewItems: [],
      resultStatementsCount: null,
      batchId: null,
      isApproving: false,
      errorMessage: null,
      searchTimeout: null,
      companySearchQuery: '',
      companySearchResults: [],
      isSearchingCompany: false,
      hasSearchedCompany: false,
      selectedCompany: null,
      companyEmployees: [],
      selectedContact: null,
      contactName: '',
      isLoadingEmployees: false,
      companySearchTimeout: null
    }
  },
  computed: {
    allChecked: function () {
      return this.reviewItems.length > 0 && this.reviewItems.every(function (i) { return i.checked })
    },
    checkedCount: function () {
      return this.reviewItems.filter(function (i) { return i.checked }).length
    }
  },
  watch: {
    recording: function () {
      // Reset all state when a new recording arrives (component may be reused)
      this.step = 'callType'
      this.callType = null
      this.callSubtype = null
      this.searchQuery = ''
      this.searchResults = []
      this.hintSuggestions = []
      this.isSearching = false
      this.hasSearched = false
      this.selectedApplicant = null
      this.offers = []
      this.selectedOfferId = null
      this.errorMessage = null
      this.companySearchQuery = ''
      this.companySearchResults = []
      this.isSearchingCompany = false
      this.hasSearchedCompany = false
      this.selectedCompany = null
      this.companyEmployees = []
      this.selectedContact = null
      this.contactName = ''
    }
  },
  created: function () {
    if (this.startAtReview && this.initialReviewItems.length > 0) {
      // Open directly in review mode
      this.reviewItems = this.initialReviewItems.map(function (item) {
        return Object.assign({}, item, { checked: true })
      })
      this.resultStatementsCount = this.initialStatementsCount
      this.selectedApplicant = this.selectedApplicantData
      this.step = 'review'
    }
  },
  methods: {
    selectCallType: function (type) {
      this.callType = type
      this.errorMessage = null
      if (type === 'applicant') {
        this.step = 'search'
        if (this.recording.hint_value) {
          this.searchQuery = this.recording.hint_value
          this.performSearch(this.recording.hint_value, true)
        }
      } else {
        this.step = 'employer_search'
      }
    },

    onSearchInput: function () {
      var self = this
      if (self.searchTimeout) clearTimeout(self.searchTimeout)
      self.hintSuggestions = []
      if (self.searchQuery.length < 2) {
        self.searchResults = []
        self.hasSearched = false
        return
      }
      self.searchTimeout = setTimeout(function () {
        self.performSearch(self.searchQuery, false)
      }, 300)
    },

    performSearch: function (query, isHint) {
      var self = this
      self.isSearching = true
      self.hasSearched = false

      var searchParams = { full_name: query, per: 10 }

      self.$network_utils.caller.request('get', '/applicants', 'admin_headers', searchParams)
        .then(function (response) {
          var results = []
          if (Array.isArray(response)) {
            results = response
          } else if (response && response.resource_id) {
            results = [response]
          }
          self.isSearching = false
          self.hasSearched = true

          if (isHint) {
            self.hintSuggestions = results.slice(0, 3)
          } else {
            self.searchResults = results.slice(0, 10)
          }
        })
        .catch(function () {
          self.isSearching = false
          self.hasSearched = true
          if (isHint) {
            self.hintSuggestions = []
          } else {
            self.searchResults = []
          }
        })
    },

    selectApplicant: function (applicant) {
      this.selectedApplicant = applicant
      this.searchResults = []
      this.errorMessage = null
    },

    clearSelection: function () {
      this.selectedApplicant = null
      this.selectedOfferId = null
      this.offers = []
    },

    backToSearch: function () {
      this.step = 'search'
      this.clearSelection()
    },

    goToCallSubtype: function () {
      this.step = 'call_subtype'
    },

    selectCallSubtype: function (subtype) {
      this.callSubtype = subtype
      if (subtype === 'follow_up') {
        // Skip offer step — go straight to analysis with follow_up mode
        this.submitForAnalysis()
      } else {
        // Introduction — continue to offer selection
        this.goToOfferStep()
      }
    },

    goToOfferStep: function () {
      var self = this
      self.step = 'offer'
      self.isLoadingOffers = true

      self.$network_utils.getResourceOffers({
        resource_type: 'Applicant',
        resource_id: self.selectedApplicant.resource_id
      })
        .then(function (response) {
          self.offers = Array.isArray(response) ? response : (response.offers || [])
          self.isLoadingOffers = false
        })
        .catch(function () {
          self.offers = []
          self.isLoadingOffers = false
        })
    },

    skipRecording: function () {
      var self = this
      self.isSkipping = true

      self.$network_utils.skipRecording({
        recording_path: self.recording.path
      })
        .then(function () {
          self.isSkipping = false
          self.onComplete()
        })
        .catch(function (err) {
          self.isSkipping = false
          self.errorMessage = (err && err.response && err.response.data && err.response.data.error) || 'שגיאה בדילוג'
        })
    },

    submitForAnalysis: function () {
      var self = this

      var isFollowUp = self.callSubtype === 'follow_up'
      var submitParams = {
        recording_path: self.recording.path,
        applicant_id: self.selectedApplicant.resource_id,
        offer_id: self.selectedOfferId,
        operation: 'rebase',
        _applicantData: self.selectedApplicant
      }
      if (isFollowUp) {
        submitParams.mode = 'follow_up'
      }

      if (self.onSubmitForAnalysis) {
        // Delegate to store — closes this modal, opens review modal when ready
        self.onSubmitForAnalysis(submitParams)
      } else {
        // Fallback — run inline
        self.errorMessage = null
        self.step = 'processing'

        self.$network_utils.submitRecordingForAnalysis(submitParams)
          .then(function (response) {
            var items = response.items || []
            self.resultStatementsCount = response.statements_count
            self.batchId = response.batch_id

            if (items.length === 0) {
              self.$utils.emit('show_toast', { text: 'לא זוהו פריטים בשיחה — סיכום השיחה נשמר בהערות פנימיות', type: 'info' })
              self.onComplete()
              return
            }

            self.reviewItems = items.map(function (item) {
              return Object.assign({}, item, { checked: true })
            })
            self.step = 'review'
          })
          .catch(function (error) {
            console.error('[RecordingDetected] Submit error:', error)
            var msg = (error && error.response && error.response.data && error.response.data.error) || 'שגיאה בניתוח השיחה'
            self.errorMessage = msg
            self.step = 'offer'
          })
      }
    },

    onCompanySearchInput: function () {
      var self = this
      if (self.companySearchTimeout) clearTimeout(self.companySearchTimeout)
      if (self.companySearchQuery.length < 2) {
        self.companySearchResults = []
        self.hasSearchedCompany = false
        return
      }
      self.companySearchTimeout = setTimeout(function () {
        self.isSearchingCompany = true
        self.hasSearchedCompany = false
        self.$network_utils.searchCompany({ name: self.companySearchQuery })
          .then(function (response) {
            var results = []
            if (Array.isArray(response)) {
              results = response
            } else if (response && response.resource_id) {
              results = [response]
            }
            self.isSearchingCompany = false
            self.hasSearchedCompany = true
            self.companySearchResults = results.slice(0, 10)
          })
          .catch(function () {
            self.isSearchingCompany = false
            self.hasSearchedCompany = true
            self.companySearchResults = []
          })
      }, 300)
    },

    selectCompany: function (company) {
      var self = this
      self.selectedCompany = company
      self.companySearchResults = []
      self.errorMessage = null

      // Load employees
      self.isLoadingEmployees = true
      self.$network_utils.getCompanyEmployees({ company_id: company.resource_id })
        .then(function (employees) {
          self.companyEmployees = Array.isArray(employees) ? employees : []
          self.isLoadingEmployees = false
        })
        .catch(function () {
          self.companyEmployees = []
          self.isLoadingEmployees = false
        })
    },

    clearCompanySelection: function () {
      this.selectedCompany = null
      this.companyEmployees = []
      this.selectedContact = null
      this.contactName = ''
    },

    selectContact: function (employee) {
      this.selectedContact = employee
      this.contactName = ''
    },

    submitEmployerCall: function () {
      var self = this
      var finalContactName = self.selectedContact ? self.selectedContact.name : self.contactName

      if (self.onSubmitForAnalysis) {
        self.onSubmitForAnalysis({
          recording_path: self.recording.path,
          company_id: self.selectedCompany.resource_id,
          contact_name: finalContactName,
          _callType: 'employer',
          _companyData: self.selectedCompany
        })
      }
    },

    approveItems: function () {
      var self = this
      self.isApproving = true
      self.errorMessage = null

      var approved = self.reviewItems
        .filter(function (i) { return i.checked })
        .map(function (i) { return { id: i.id, value: i.value } })

      var rejected = self.reviewItems
        .filter(function (i) { return !i.checked })
        .map(function (i) { return i.id })

      self.$network_utils.approveConversationAnalysis({
        approved_items: approved,
        rejected_ids: rejected
      })
        .then(function (response) {
          self.isApproving = false
          self.$utils.emit('show_toast', {
            text: 'עובדו ' + response.processed + ' פריטים',
            type: 'success'
          })
          self.onComplete()
        })
        .catch(function (error) {
          self.isApproving = false
          console.error('[RecordingDetected] Approve error:', error)
          self.errorMessage = (error && error.response && error.response.data && error.response.data.error) || 'שגיאה באישור הפריטים'
        })
    },

    toggleAll: function () {
      var newVal = !this.allChecked
      this.reviewItems.forEach(function (i) { i.checked = newVal })
    },

    formatResourceType: function (type) {
      var map = {
        Skill: 'skill',
        JobTitle: 'job_title',
        Applicant: 'applicant',
        Offer: 'offer'
      }
      return map[type] || type
    },
    isToggleable: function (value) {
      var v = (value || '').toUpperCase()
      return v === 'TRUE' || v === 'FALSE' || v === 'FEMALE' || v === 'MALE'
    },
    toggleValue: function (item) {
      var v = (item.value || '').toUpperCase()
      if (v === 'TRUE') item.value = 'FALSE'
      else if (v === 'FALSE') item.value = 'TRUE'
      else if (v === 'FEMALE') item.value = 'MALE'
      else if (v === 'MALE') item.value = 'FEMALE'
    }
  }
}
</script>

<style scoped>
.recording-detected-modal {
  padding: 1.5rem;
  min-height: 200px;
}

/* Step Header */
.step-header {
  margin-bottom: 1rem;
}

.recording-filename {
  font-size: 0.9rem;
  color: #868e96;
  background: #f8f9fa;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  direction: ltr;
  text-align: left;
}

/* Hint Section */
.hint-section {
  margin-bottom: 1rem;
}

.hint-label {
  font-size: 0.85rem;
  color: #495057;
  display: block;
  margin-bottom: 0.5rem;
}

.hint-chips {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.hint-chip {
  background: linear-gradient(135deg, #667eea20 0%, #764ba220 100%);
  border: 2px solid #667eea;
  color: #2c3e50;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.hint-chip:hover {
  background: linear-gradient(135deg, #667eea40 0%, #764ba240 100%);
}

.hint-phone {
  font-size: 0.8rem;
  color: #868e96;
  margin-right: 0.5rem;
  direction: ltr;
  display: inline-block;
}

/* Search Section */
.search-section {
  margin-bottom: 1rem;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  font-size: 1rem;
  direction: rtl;
  transition: border-color 0.2s;
  background: #fff !important;
  color: #2c3e50 !important;
  -webkit-text-fill-color: #2c3e50 !important;
  opacity: 1 !important;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
}

.search-loading {
  text-align: center;
  padding: 1rem;
  color: #868e96;
}

.search-results {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  margin-top: 0.5rem;
  max-height: 250px;
  overflow-y: auto;
}

.search-result-item {
  padding: 0.6rem 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border-bottom: 1px solid #f1f3f5;
  transition: background 0.15s;
}

.search-result-item:hover {
  background: #f8f9fa;
}

.search-result-item:last-child {
  border-bottom: none;
}

.result-name {
  font-weight: 600;
  color: #2c3e50;
}

.result-phone {
  font-size: 0.85rem;
  color: #868e96;
  direction: ltr;
}

.result-status {
  font-size: 0.8rem;
  color: #adb5bd;
  margin-right: auto;
}

.no-results {
  text-align: center;
  padding: 1rem;
  color: #868e96;
}

/* Selected Section */
.selected-section {
  margin-bottom: 1rem;
}

.selected-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #667eea;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.95rem;
}

.selected-chip.compact {
  margin-bottom: 1rem;
}

.chip-remove {
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0 0.25rem;
  line-height: 1;
  opacity: 0.8;
}

.chip-remove:hover {
  opacity: 1;
}

/* Offer Step */
.offer-label {
  font-size: 1rem;
  color: #495057;
  margin-bottom: 0.75rem;
}

.offer-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  max-height: 250px;
  overflow-y: auto;
}

.offer-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
}

.offer-item:hover {
  border-color: #667eea;
  background: #f8f9ff;
}

.offer-selected {
  border-color: #667eea;
  background: #f0f2ff;
}

.offer-info {
  display: flex;
  flex-direction: column;
}

.offer-position {
  font-weight: 600;
  color: #2c3e50;
}

.offer-company {
  font-size: 0.85rem;
  color: #868e96;
}

.offer-status {
  font-size: 0.8rem;
  color: #adb5bd;
}

.no-offers {
  text-align: center;
  padding: 1rem;
  color: #868e96;
  margin-bottom: 1rem;
}

/* Step Actions */
.step-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-start;
  padding-top: 0.5rem;
}

/* Processing Step */
.step-processing {
  display: flex;
  justify-content: center;
  padding: 3rem 0;
}

.processing-indicator {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e9ecef;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.processing-text {
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.processing-sub {
  font-size: 0.85rem;
  color: #868e96;
  margin: 0;
}

/* Review Step */
.step-review {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.review-title {
  font-size: 0.95rem;
  color: #495057;
}

.review-applicant-link {
  display: inline;
}

.review-applicant-link .link-entity {
  font-weight: 700;
}

.review-summary {
  font-size: 0.95rem;
  color: #495057;
  padding: 0.5rem 0;
}

.table-wrapper {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.review-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.review-table thead {
  position: sticky;
  top: 0;
  background: #f8f9fa;
  z-index: 1;
}

.review-table th,
.review-table td {
  padding: 0.6rem 0.75rem;
  text-align: right;
  border-bottom: 1px solid #dee2e6;
}

.review-table th {
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
}

.review-table tbody tr:hover {
  background: #f8f9fa;
}

.row-unchecked {
  opacity: 0.5;
}

.col-check {
  width: 40px;
  text-align: center !important;
}

.col-type { width: 80px; }
.col-name { width: 120px; }
.col-field { width: 120px; }
.col-value { min-width: 200px; }
.col-source { min-width: 180px; }

.value-input {
  width: 100%;
  padding: 0.25rem 0.4rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.85rem;
  direction: rtl;
  color: #2c3e50 !important;
  background: #fff !important;
  -webkit-text-fill-color: #2c3e50 !important;
}

.value-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

.value-toggle {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  background: #e9ecef;
  color: #495057;
  border: 1px solid #ced4da;
  transition: background 0.15s;
}

.value-toggle:hover {
  background: #dee2e6;
}

.source-text {
  font-size: 0.8rem;
  color: #868e96;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.review-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-start;
  padding-top: 0.5rem;
}

/* Buttons */
.btn {
  padding: 0.6rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:not(:disabled):hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.btn-secondary {
  background: #e9ecef;
  color: #495057;
}

.btn-secondary:not(:disabled):hover {
  background: #dee2e6;
}

.btn-outline {
  background: transparent;
  color: #667eea;
  border: 2px solid #667eea;
}

.btn-outline:not(:disabled):hover {
  background: #f0f2ff;
}

.error-message {
  background: #fff5f5;
  color: #c92a2a;
  padding: 0.75rem;
  border-radius: 6px;
  text-align: center;
  margin-top: 1rem;
}

/* Call Type Step */
.call-type-label {
  font-size: 1.1rem;
  color: #495057;
  margin-bottom: 1rem;
  font-weight: 600;
}

.call-type-buttons {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.call-type-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem 1rem;
  border: 2px solid #dee2e6;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  transition: all 0.2s;
}

.call-type-btn:hover {
  border-color: #667eea;
  background: #f8f9ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}

.call-type-icon {
  font-size: 2rem;
}

.call-type-desc {
  font-size: 0.75rem;
  color: #868e96;
  font-weight: 400;
}

/* Employer Search Step */
.step-employer-search .contact-section {
  margin-top: 1rem;
}

.contact-label {
  font-size: 0.95rem;
  color: #495057;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.employee-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}

.employee-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border-bottom: 1px solid #f1f3f5;
  transition: background 0.15s;
}

.employee-item:hover {
  background: #f8f9fa;
}

.employee-item:last-child {
  border-bottom: none;
}

.employee-selected {
  background: #f0f2ff;
  border-color: #667eea;
}

.employee-name {
  font-weight: 600;
  color: #2c3e50;
}

.employee-title {
  font-size: 0.85rem;
  color: #868e96;
}

.no-employees {
  text-align: center;
  padding: 0.75rem;
  color: #868e96;
  font-size: 0.9rem;
}

.free-contact {
  margin-top: 0.75rem;
}

.free-contact-label {
  font-size: 0.85rem;
  color: #495057;
  margin-bottom: 0.25rem;
  display: block;
}

.contact-input {
  margin-top: 0.25rem;
}
</style>
